<template>
  <div class="training-week-stats">
    <!-- This Week Summary -->
    <div class="week-summary">
      <div class="summary-chips">
        <div class="chip chip-team">
          <i class="bi bi-people-fill"></i>
          <span class="chip-count">{{ weekCounts.team }}</span>
          <span class="chip-label">Team</span>
        </div>
        <div class="chip chip-own">
          <i class="bi bi-person-fill"></i>
          <span class="chip-count">{{ weekCounts.own }}</span>
          <span class="chip-label">Own</span>
        </div>
        <div class="chip chip-sc">
          <i class="bi bi-lightning-fill"></i>
          <span class="chip-count">{{ weekCounts.sc }}</span>
          <span class="chip-label">S&C</span>
        </div>
        <div class="chip chip-load">
          <i class="bi bi-bar-chart-fill"></i>
          <span class="chip-count">{{ weekLoad }}</span>
          <span class="chip-label">Load</span>
        </div>
      </div>
    </div>

    <!-- Season Overview -->
    <div class="season-section">
      <div class="season-header">
        <h4><i class="bi bi-graph-up"></i> Season Overview</h4>
      </div>

      <div class="season-stats">
        <!-- Games Attendance -->
        <div class="attendance-group">
          <h5><i class="bi bi-trophy"></i> Games</h5>
          <div class="stat-row">
            <div class="season-stat">
              <span class="stat-number">{{ gamesAttendance.total }}</span>
              <span class="stat-label">Total</span>
            </div>
            <div class="season-stat stat-green">
              <span class="stat-number">{{ gamesAttendance.attended }}</span>
              <span class="stat-label">Attended</span>
            </div>
            <div class="season-stat stat-amber">
              <span class="stat-number">{{ gamesAttendance.noResponse }}</span>
              <span class="stat-label">No Response</span>
            </div>
          </div>
        </div>

        <!-- Training Attendance -->
        <div class="attendance-group">
          <h5><i class="bi bi-clipboard-check"></i> Training</h5>
          <div class="stat-row">
            <div class="season-stat">
              <span class="stat-number">{{ trainingAttendance.total }}</span>
              <span class="stat-label">Total</span>
            </div>
            <div class="season-stat stat-green">
              <span class="stat-number">{{ trainingAttendance.attended }}</span>
              <span class="stat-label">Attended</span>
            </div>
            <div class="season-stat stat-amber">
              <span class="stat-number">{{ trainingAttendance.noResponse }}</span>
              <span class="stat-label">No Response</span>
            </div>
          </div>
        </div>

        <!-- Session Type Breakdown -->
        <div class="stat-row">
          <div class="season-stat">
            <span class="stat-number">{{ seasonTotals.team }}</span>
            <span class="stat-label">Team</span>
          </div>
          <div class="season-stat">
            <span class="stat-number">{{ seasonTotals.own }}</span>
            <span class="stat-label">Own</span>
          </div>
          <div class="season-stat">
            <span class="stat-number">{{ seasonTotals.sc }}</span>
            <span class="stat-label">S&C</span>
          </div>
          <div class="season-stat">
            <span class="stat-number">{{ seasonTotals.avgRpe || '-' }}</span>
            <span class="stat-label">Avg RPE</span>
          </div>
        </div>

        <!-- Session RPE Timeline -->
        <div v-if="sessionTimeline.length > 0" class="rpe-timeline">
          <h5>Session RPE Timeline</h5>
          <div class="rpe-bar-chart">
            <div class="rpe-y-axis">
              <span>10</span>
              <span>8</span>
              <span>6</span>
              <span>4</span>
              <span>2</span>
              <span>0</span>
            </div>
            <div class="rpe-bars-area">
              <div class="rpe-grid-lines">
                <div class="rpe-grid-line" v-for="n in 5" :key="n" :style="{ bottom: (n * 20) + '%' }"></div>
              </div>
              <div class="rpe-bars-scroll">
                <div
                  v-for="(session, idx) in sessionTimeline"
                  :key="idx"
                  class="rpe-bar-col"
                  :title="`${session.created_date}\nRPE: ${session.effort}/10\n${sessionTypeLabel(session)}\n${session.enjoyment || ''}`"
                >
                  <div class="rpe-bar-wrapper">
                    <div
                      class="rpe-bar"
                      :style="{ height: (parseInt(session.effort) / 10 * 100) + '%', background: rpeBarGradient(session.effort) }"
                    >
                      <span class="rpe-bar-value">{{ session.effort }}</span>
                    </div>
                  </div>
                  <div class="rpe-bar-type-dot" :style="{ background: dotColor(session) }"></div>
                  <span class="rpe-bar-date">{{ shortDate(session.created_date) }}</span>
                </div>
              </div>
            </div>
          </div>
          <!-- Legend -->
          <div class="timeline-legend">
            <span class="legend-item"><span class="legend-dot" style="background:#3b82f6"></span> Team</span>
            <span class="legend-item"><span class="legend-dot" style="background:#22c55e"></span> Own</span>
            <span class="legend-item"><span class="legend-dot" style="background:#f59e0b"></span> S&C</span>
          </div>
        </div>

        <!-- Weekly Load Trend -->
        <div v-if="weeklyLoadData.length > 0" class="load-trend">
          <h5>Weekly Load Trend</h5>
          <div class="mini-chart">
            <div
              v-for="(week, idx) in weeklyLoadData"
              :key="idx"
              class="bar-col"
              :title="`Week: ${week.weekly_load} load`"
            >
              <div class="bar" :style="{ height: barHeight(week.weekly_load) + '%' }"></div>
              <span class="bar-label">{{ week.session_count }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TrainingWeekStats",
  props: {
    weekSessions: { type: Array, default: () => [] },
    seasonData: { type: Object, default: () => ({ byType: [], weeklyLoad: [] }) },
  },
  data() {
    return {};
  },
  computed: {
    weekCounts() {
      const trainingSessions = this.weekSessions.filter(s => s.gameType === "Training");
      return {
        team: trainingSessions.filter(s => (s.training_session_type || "Team Training") === "Team Training").length,
        own: trainingSessions.filter(s => s.training_session_type === "Own Training").length,
        sc: trainingSessions.filter(s => s.training_session_type === "S&C").length,
      };
    },
    weekLoad() {
      return this.weekSessions
        .filter(s => s.effort)
        .reduce((sum, s) => sum + parseInt(s.effort), 0);
    },
    seasonTotals() {
      const byType = this.seasonData.byType || [];
      const getType = (type) => byType.find(t => (t.training_session_type || "Team Training") === type) || {};

      const team = getType("Team Training");
      const own = getType("Own Training");
      const sc = getType("S&C");

      const totalReports = (team.reports_count || 0) + (own.reports_count || 0) + (sc.reports_count || 0);
      const totalSessions = (team.sessions_attended || 0) + (own.sessions_attended || 0) + (sc.sessions_attended || 0);
      const totalMinutes = (team.total_minutes || 0) + (own.total_minutes || 0) + (sc.total_minutes || 0);

      // Weighted average RPE
      const avgValues = byType.filter(t => t.avg_effort).map(t => parseFloat(t.avg_effort));
      const avgRpe = avgValues.length > 0
        ? (avgValues.reduce((a, b) => a + b, 0) / avgValues.length).toFixed(1)
        : null;

      return {
        team: team.sessions_attended || 0,
        own: own.sessions_attended || 0,
        sc: sc.sessions_attended || 0,
        avgRpe,
        totalHours: Math.round(totalMinutes / 60),
        attendance: totalSessions > 0 ? Math.round((totalReports / totalSessions) * 100) : 0,
      };
    },
    gamesAttendance() {
      const att = (this.seasonData.attendance || {}).games || {};
      return {
        total: parseInt(att.total) || 0,
        attended: parseInt(att.attended) || 0,
        noResponse: parseInt(att.no_response) || 0,
      };
    },
    trainingAttendance() {
      const att = (this.seasonData.attendance || {}).training || {};
      return {
        total: parseInt(att.total) || 0,
        attended: parseInt(att.attended) || 0,
        noResponse: parseInt(att.no_response) || 0,
      };
    },
    sessionTimeline() {
      return this.seasonData.sessionTimeline || [];
    },
    weeklyLoadData() {
      const data = this.seasonData.weeklyLoad || [];
      return data.slice(-12); // Last 12 weeks
    },
    maxLoad() {
      return Math.max(...this.weeklyLoadData.map(w => parseInt(w.weekly_load) || 0), 1);
    },
  },
  methods: {
    barHeight(load) {
      return Math.round((parseInt(load) / this.maxLoad) * 100);
    },
    rpeBarGradient(effort) {
      const n = parseInt(effort);
      if (n <= 2) return "linear-gradient(180deg, #93c5fd, #3b82f6)";
      if (n <= 4) return "linear-gradient(180deg, #86efac, #22c55e)";
      if (n <= 6) return "linear-gradient(180deg, #fde047, #eab308)";
      if (n <= 8) return "linear-gradient(180deg, #fdba74, #f97316)";
      return "linear-gradient(180deg, #fca5a5, #ef4444)";
    },
    dotColor(session) {
      const type = session.training_session_type || session.game_session_type || "Team Training";
      if (type === "Own Training") return "#22c55e";
      if (type === "S&C") return "#f59e0b";
      return "#3b82f6";
    },
    sessionTypeLabel(session) {
      return session.training_session_type || session.game_session_type || "Team Training";
    },
    shortDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return `${d.getDate()}/${d.getMonth() + 1}`;
    },
  },
};
</script>

<style scoped lang="scss">
.training-week-stats {
  margin-top: 16px;
}

.week-summary {
  margin-bottom: 12px;
}

.summary-chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 10px;
  flex: 1;
  min-width: 80px;

  i { font-size: 0.9rem; }
  .chip-count { font-size: 1.1rem; font-weight: 700; }
  .chip-label { font-size: 0.68rem; color: #6b7280; font-weight: 500; }

  &.chip-team { background: #eff6ff; color: #3b82f6; }
  &.chip-own { background: #f0fdf4; color: #22c55e; }
  &.chip-sc { background: #fffbeb; color: #f59e0b; }
  &.chip-load { background: #f3f4f6; color: #4b5563; }
}

.season-section {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
}

.season-header {
  padding: 12px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;

  h4 {
    margin: 0;
    font-size: 0.92rem;
    font-weight: 600;
    color: #374151;
    display: flex;
    align-items: center;
    gap: 6px;
    i { color: #3b82f6; }
  }
}

.season-stats {
  padding: 16px;
}

.attendance-group {
  margin-bottom: 12px;

  h5 {
    font-size: 0.78rem;
    font-weight: 600;
    color: #4b5563;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 5px;

    i { font-size: 0.8rem; color: #6b7280; }
  }
}

.stat-row {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.season-stat {
  flex: 1;
  text-align: center;
  padding: 10px 8px;
  background: #f9fafb;
  border-radius: 8px;

  .stat-number {
    display: block;
    font-size: 1.3rem;
    font-weight: 700;
    color: #1f2937;
  }

  .stat-label {
    display: block;
    font-size: 0.65rem;
    color: #9ca3af;
    font-weight: 600;
    text-transform: uppercase;
    margin-top: 2px;
  }

  &.stat-green { background: #f0fdf4; .stat-number { color: #16a34a; } }
  &.stat-amber { background: #fffbeb; .stat-number { color: #d97706; } }
  &.stat-red { background: #fef2f2; .stat-number { color: #dc2626; } }
}

/* RPE Bar Chart */
.rpe-timeline {
  margin-bottom: 16px;

  h5 {
    font-size: 0.8rem;
    font-weight: 600;
    color: #6b7280;
    margin-bottom: 8px;
  }
}

.rpe-bar-chart {
  display: flex;
  gap: 4px;
  height: 160px;
}

.rpe-y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0 4px 28px 0;
  width: 22px;
  flex-shrink: 0;

  span {
    font-size: 0.6rem;
    color: #9ca3af;
    text-align: right;
    line-height: 1;
  }
}

.rpe-bars-area {
  flex: 1;
  position: relative;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

.rpe-grid-lines {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 28px;
  pointer-events: none;
}

.rpe-grid-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: #f0f0f0;
}

.rpe-bars-scroll {
  display: flex;
  gap: 3px;
  align-items: flex-end;
  height: 100%;
  min-width: min-content;
  padding-bottom: 28px;
}

.rpe-bar-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 32px;
  flex-shrink: 0;
  height: 100%;
  cursor: default;
  position: relative;
}

.rpe-bar-wrapper {
  flex: 1;
  display: flex;
  align-items: flex-end;
  width: 100%;
}

.rpe-bar {
  width: 100%;
  border-radius: 4px 4px 0 0;
  min-height: 4px;
  transition: height 0.3s ease;
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.rpe-bar-value {
  font-size: 0.6rem;
  font-weight: 800;
  color: #fff;
  padding-top: 3px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.rpe-bar-type-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-top: 3px;
  flex-shrink: 0;
}

.rpe-bar-date {
  font-size: 0.5rem;
  color: #9ca3af;
  white-space: nowrap;
  margin-top: 2px;
}

.timeline-legend {
  display: flex;
  gap: 12px;
  margin-top: 8px;
  justify-content: center;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.65rem;
  color: #6b7280;
  font-weight: 500;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.load-trend {
  margin-top: 8px;

  h5 {
    font-size: 0.8rem;
    font-weight: 600;
    color: #6b7280;
    margin-bottom: 8px;
  }
}

.mini-chart {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 60px;
}

.bar-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  justify-content: flex-end;
}

.bar {
  width: 100%;
  background: linear-gradient(180deg, #3b82f6, #60a5fa);
  border-radius: 3px 3px 0 0;
  min-height: 2px;
  transition: height 0.3s ease;
}

.bar-label {
  font-size: 0.55rem;
  color: #9ca3af;
  margin-top: 2px;
}

@media (max-width: 480px) {
  .summary-chips {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }
  .chip { padding: 8px 12px; min-width: 0; }
  .stat-row { gap: 6px; }
}
</style>
